import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import i18n from 'i18next';
import { commonApi } from 'shared/api/commonApi';
import { CATEGORIES, COUNTRIES, DEFAULT_LANGUAGE, dynamicTrans } from 'shared/consts';
import { ELanguages, IAppConfig, IMeta, TLabelValue, TOffersMeta } from 'shared/typings';
import { getOptionsFromSlug, updateOptionsFromText } from 'shared/utils/getOptions';

interface ICommonSlice {
	notificationsModalIsOpened: boolean;
	categoryOptions: TLabelValue[];
	countryOptions: TLabelValue[];
	trafficTypeOptions: TLabelValue[];
	websiteSourceTypeOptions: TLabelValue[];
	bloggerSourceTypeOptions: TLabelValue[];
	contentThemesOptions: TLabelValue[];
	tagsOptions: TLabelValue[];
	payoutModelsOptions: TLabelValue[];
	operationSystemsOptions: TLabelValue[];
	balanceCurrencies: TLabelValue[];
	payableEventsMap: Record<string, string>;
	offersMap: Record<string, string>;
	currenciesMap: Record<string, string>;
	trafficTypesMap: Record<string, string>;
	trans: ReturnType<typeof dynamicTrans>;
	infinityScrollOffset: number;
	infinityScrollPage: number;
	language: ELanguages;
}

const initialState: ICommonSlice = {
	notificationsModalIsOpened: false,
	categoryOptions: [],
	countryOptions: [],
	trafficTypeOptions: [],
	websiteSourceTypeOptions: [],
	bloggerSourceTypeOptions: [],
	contentThemesOptions: [],
	tagsOptions: [],
	payoutModelsOptions: [],
	operationSystemsOptions: [],
	balanceCurrencies: [],
	payableEventsMap: {},
	currenciesMap: {},
	trafficTypesMap: {},
	offersMap: {},
	trans: dynamicTrans(),
	infinityScrollOffset: 0,
	infinityScrollPage: 1,
	language: ELanguages.EN,
};

export const commonSlice = createSlice({
	name: 'common',
	initialState,
	reducers: {
		setNotificationsModalIsOpened: (state, { payload }: PayloadAction<boolean>) => {
			state.notificationsModalIsOpened = payload;
		},
		setInfinityScrollOffset: (state, action: PayloadAction<number>) => {
			state.infinityScrollOffset = action.payload;
		},
		setInfinityScrollPage: (state, action: PayloadAction<number>) => {
			state.infinityScrollPage = action.payload;
		},
		clearInfinityScroll: (state) => {
			state.infinityScrollOffset = 0;
			state.infinityScrollPage = 1;
		},
		changeOffersMap: (state, action: PayloadAction<{ key: string; name: string }>) => {
			state.offersMap = { ...state.offersMap, [action.payload.key]: action.payload.name };
		},
		setLanguage: (state, action: PayloadAction<ELanguages>) => {
			state.language = action.payload;
		},
		setTrans: (state, action: PayloadAction<ReturnType<typeof dynamicTrans>>) => {
			state.trans = action.payload;
			state.categoryOptions = updateOptionsFromText(state.categoryOptions, CATEGORIES);
			state.countryOptions = updateOptionsFromText(state.countryOptions, COUNTRIES);
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(commonApi.endpoints?.getMeta.matchFulfilled, (state, action: PayloadAction<IMeta>) => {
			const data = action.payload;
			state.categoryOptions = getOptionsFromSlug(data.categories, CATEGORIES);

			state.trafficTypeOptions = data.traffic_types
				.map(({ slug, name }) => ({
					value: slug,
					label: name,
				}))
				.sort((a, b) => a.label?.localeCompare(b.label));

			state.websiteSourceTypeOptions = data.website_platform_types
				.map(({ slug, name }) => ({
					value: slug,
					label: name,
				}))
				.sort((a, b) => a.label?.localeCompare(b.label));

			state.bloggerSourceTypeOptions = data.influencer_platform_types
				.map(({ slug, name }) => ({
					value: slug,
					label: name,
				}))
				.sort((a, b) => a.label?.localeCompare(b.label));

			state.contentThemesOptions = data.content_themes
				.map(({ slug, name }) => ({
					value: slug,
					label: name,
				}))
				.sort((a, b) => a.label?.localeCompare(b.label));

			state.countryOptions = updateOptionsFromText(
				data.countries.map(({ alpha_2, name }) => ({
					value: alpha_2,
					label: name,
				})),
				COUNTRIES,
			);

			state.tagsOptions = data.tags.map(({ slug, name }) => ({
				value: slug,
				label: name,
			}));

			state.balanceCurrencies = data.balance_currencies
				.map(({ code, sign }) => ({
					value: code,
					label: `${code}, ${sign}`,
				}))
				.sort((a, b) => a.label?.localeCompare(b.label));

			state.currenciesMap = data.balance_currencies.reduce((acc, { code, sign }) => {
				return { ...acc, [code]: sign };
			}, {});

			state.trafficTypesMap = data.traffic_types.reduce((acc, { slug, name }) => {
				return { ...acc, [slug]: name };
			}, {});
		});
		builder.addMatcher(
			commonApi.endpoints?.getOffersMeta.matchFulfilled,
			(state, action: PayloadAction<TOffersMeta>) => {
				const data = action.payload;
				const offersFromParams =
					data.results?.reduce((acc, offer) => {
						return { ...acc, [offer.uuid]: offer.name };
					}, {}) || {};

				state.offersMap = { ...state.offersMap, ...offersFromParams };
			},
		);
		builder.addMatcher(commonApi.endpoints?.getAppConfig.matchFulfilled, (state, action: PayloadAction<IAppConfig>) => {
			const lang = action.payload.user?.language
				? DEFAULT_LANGUAGE(action.payload.user.language)
				: DEFAULT_LANGUAGE(i18n.language as ELanguages);
			state.language = lang;
			localStorage.setItem('user_id', action.payload.user?.user_id || '');
		});
	},
});

export default commonSlice.reducer;
export const {
	changeOffersMap,
	setTrans,
	setInfinityScrollOffset,
	setInfinityScrollPage,
	clearInfinityScroll,
	setNotificationsModalIsOpened,
} = commonSlice.actions;
